import type { ReactNode, Ref } from 'react';
import { Search } from 'lucide-react';
import { IconButton, Input } from '@/components/ui';

interface MasterLookupBarProps {
  /** What is being looked up — "Period Code", "Bank Code". */
  label: string;
  /** The controls. Usually one MasterCodeLookup; ExchangeRate composes three fields. */
  children: ReactNode;
  /** Shown once a record is loaded — its name and status. */
  summary?: ReactNode;
  /** Guidance shown only while nothing is loaded. */
  hint?: ReactNode;
}

/**
 * The strip under the toolbar where a master record is found by its code.
 *
 * All eight screens had one and no two were the same: the bar was `px-5 py-2` on six and
 * `px-4 py-1.5` on another, the label was `text-xs` semibold grey-500 on six and a `text-[10px]`
 * uppercase grey-400 elsewhere, and the label reserved w-10, w-14 or w-20 so the field it labels
 * started at a different place on every screen. The label width is fixed here so the input lines
 * up whichever master you open.
 */
export const MasterLookupBar = ({ label, children, summary, hint }: MasterLookupBarProps) => (
  <div data-testid="master-lookup-bar" className="flex-shrink-0 flex flex-wrap items-center gap-3 px-5 py-2 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
    <span className="w-24 flex-shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <div className="flex items-center gap-2">{children}</div>
    {summary && <div className="flex items-center gap-2 ml-2">{summary}</div>}
    {hint && <span className="ml-2 text-xs italic text-gray-400 dark:text-gray-500">{hint}</span>}
  </div>
);

interface MasterCodeLookupProps {
  inputRef?: Ref<HTMLInputElement>;
  value: string;
  /** Receives the already-uppercased value: every master code on this system is upper case. */
  onChange: (value: string) => void;
  /** Enter pressed in the field. */
  onSubmit: () => void;
  onBrowse: () => void;
  /** Accessible name for the browse button, e.g. "Search periods". */
  browseLabel: string;
  placeholder: string;
  maxLength?: number;
  disabled?: boolean;
  busy?: boolean;
}

/** Type a code and press Enter, or browse — the pattern on every master screen. */
export const MasterCodeLookup = ({
  inputRef, value, onChange, onSubmit, onBrowse, browseLabel,
  placeholder, maxLength = 20, disabled, busy,
}: MasterCodeLookupProps) => (
  <>
    <Input
      ref={inputRef}
      data-testid="master-code-input"
      size="sm"
      type="text"
      value={value}
      onChange={e => onChange(e.target.value.toUpperCase())}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
      disabled={disabled}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-40 font-mono uppercase"
    />
    <IconButton
      label={browseLabel}
      icon={<Search size={14} />}
      loading={busy}
      onClick={onBrowse}
      disabled={disabled}
    />
  </>
);
